import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Avatar,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import axios from "../../api/axiosInstance";
import { getHeader } from "../../types";

const COLUMNS = [
  { key: "uniqueJobs", label: "Unique Jobs" },
  { key: "applied", label: "Applied" },
  { key: "curated", label: "Curated" },
  { key: "shortlisted", label: "Shortlisted" },
  { key: "interviewed", label: "Interviewed" },
  { key: "offered", label: "Offered" },
  { key: "hired", label: "Hired" },
  { key: "rejected", label: "Rejected" },
];

function totalOf(value) {
  if (value && typeof value === "object" && "total" in value) {
    return value.total;
  }
  return Number(value) || 0;
}

function subStages(value) {
  if (value && typeof value === "object") {
    return Object.entries(value).filter(
      ([key, val]) => key !== "total" && Number(val) > 0
    );
  }
  return [];
}

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function dateRangeFor(dateType, customStart, customEnd) {
  const now = new Date();
  let start = new Date(now);
  let end = new Date(now);

  if (dateType === "Daily") {
    start = new Date(now);
  } else if (dateType === "Weekly") {
    start.setDate(now.getDate() - 6);
  } else if (dateType === "Monthly") {
    start.setDate(now.getDate() - 29);
  } else if (dateType === "Custom") {
    if (customStart) start = new Date(customStart);
    if (customEnd) end = new Date(customEnd);
  }

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

export default function RolesDashboard() {
  const { company_id } = useSelector((state) => state.user.user) || {};
  const [dateType, setDateType] = useState("Weekly");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [tpFilter, setTpFilter] = useState("All");
  const [podFilter, setPodFilter] = useState("All");
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [tpOptions, setTpOptions] = useState([]);
  const [podOptions, setPodOptions] = useState([]);
  const [tpByEmail, setTpByEmail] = useState({});


  useEffect(() => {
    if (!company_id) return;
    const headers = getHeader();

    axios
      .get(`/api/auth/allPodGroup/${company_id}`, { headers })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        const cleaned = data
          .map((p) => (p && typeof p === "object" ? p.podGroup || p.pod_group : p))
          .filter((p) => typeof p === "string" && p.trim() !== "");
        setPodOptions([...new Set(cleaned)]);
      })
      .catch((err) => console.error("Failed to load pods", err));

    axios
      .get(`/api/company/member/${company_id}`, { headers })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        const map = {};
        data.forEach((u) => {
          const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
          const email = u.email || u.user_name || "";
          if (name.trim() && email) map[email] = name;
        });
        setTpByEmail(map);
        setTpOptions(
          Object.entries(map)
            .map(([email, name]) => ({ name, email }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch((err) => console.error("Failed to load talent partners", err));

    const { startDate, endDate } = dateRangeFor("Weekly");
    axios
      .post(
        "/api/dashboard/recruiter-dashboard",
        { startDate, endDate, page: 1, limit: 100 },
        { headers }
      )
      .then((res) => {
        const data = Array.isArray(res.data?.result) ? res.data.result : [];
        const names = [...new Set(data.map((r) => r.name).filter(Boolean))];
        if (names.length > 0 && Object.keys(tpByEmail).length === 0) {
          setTpOptions(names.sort().map((name) => ({ name, email: email })));
        }
      })
      .catch((err) => console.error("Failed to load dashboard names", err));
  }, [company_id]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const { startDate, endDate } = dateRangeFor(dateType, customStart, customEnd);
    const payload = {
      startDate,
      endDate,
      email: tpFilter === "All" ? undefined : tpFilter,
      pod_group: podFilter === "All" ? undefined : podFilter,
      page: 1,
      limit: 50,
    };
    if (sortBy && sortOrder) {
      payload.sortBy = sortBy;
      payload.sortOrder = sortOrder;
    }

    axios
      .post("/api/dashboard/recruiter-dashboard", payload, { headers: getHeader() })
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data?.result) ? res.data.result : [];
        setStats(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch recruiter dashboard", err);
        setError("Failed to load dashboard data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dateType, customStart, customEnd, tpFilter, podFilter, sortBy, sortOrder]);

  const pods_grouped = podOptions.sort();
  const tpNames = tpOptions;
  console.log(tpOptions, "dfkmdslkfmn", tpNames)

  const tpSelected =
    tpFilter === "All"
      ? null
      : tpNames.find((o) => o.email === tpFilter) ||
      (typeof tpFilter === "string" ? tpFilter : null);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        Recruiter Dashboard
      </Typography>

      {/* --- Filters bar, placed above the table --- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              freeSolo
              options={tpNames}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.name)}
              getOptionKey={(option) => option.email || ""}
              value={tpSelected}
              onChange={(_, val) =>
                setTpFilter(val ? (typeof val === "string" ? val : val.email) : "All")
              }
              renderInput={(params) => (
                <TextField {...params} label="Talent Partner" placeholder="Type to search"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F6C8D",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4F6C8D",
                    },
                  }} />
              )}
              sx={{ minWidth: 180 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              freeSolo
              options={pods_grouped}
              value={podFilter === "All" ? null : podFilter}
              onChange={(_, val) => setPodFilter(val || "All")}
              renderInput={(params) => (
                <TextField {...params} label="Pod" placeholder="Type to search"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F6C8D",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4F6C8D",
                    },
                  }} />
              )}
              sx={{ minWidth: 180 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={9}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ flexWrap: "wrap" }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={dateType}
                onChange={(_, val) => val && setDateType(val)}
                sx={{ flexWrap: "wrap" }}
              >
                <ToggleButton value="Daily">Daily</ToggleButton>
                <ToggleButton value="Weekly">Weekly</ToggleButton>
                <ToggleButton value="Monthly">Monthly</ToggleButton>
                <ToggleButton value="Custom">Custom</ToggleButton>
              </ToggleButtonGroup>

              {dateType === "Custom" && (
                <>
                  <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    sx={{ minWidth: 160 }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    sx={{ minWidth: 160 }}
                  />
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* --- Recruiter pipeline breakdown table --- */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#FAFAFA" }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Talent Partner</TableCell>
                {COLUMNS.map((c) => {
                  const sortKey = `${c.key}.total`;
                  const active = sortBy === sortKey;
                  return (
                    <TableCell
                      key={c.key}
                      align="center"
                      onClick={() => handleSort(sortKey)}
                      sx={{
                        fontWeight: 600,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": { color: "#38CFAB" },
                        fontSize: 13,
                      }}
                    >
                      {c.label} {active ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.map((r, i) => (
                <TableRow key={r.email || i} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: "#1F2A44" }}>
                        {initials(r.name)}
                      </Avatar>
                      <Stack spacing={0}>
                        <Typography variant="body2" fontWeight={500}>
                          {r.name}
                        </Typography>
                        {r.role && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {r.role}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </TableCell>
                  {COLUMNS.map((c) => {
                    const subs = c.key === "interviewed" ? subStages(r[c.key]) : [];
                    return (
                      <TableCell key={c.key} align="center">
                        <Typography variant="h7" fontWeight={c.key === "rejected" ? 400 : 500}>
                          {totalOf(r[c.key])}
                        </Typography>
                        {/* {subs.length > 0 && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {subs.map(([k, v]) => `${k}: ${v}`).join(", ")}
                          </Typography>
                        )} */}
                        {subs.length > 0 && (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {subs.map(([k, v]) => (
                              <div key={k}>
                                {k}: {v}
                              </div>
                            ))}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {!loading && error && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 4, color: "error.main" }}>
                    {error}
                  </TableCell>
                </TableRow>
              )}

              {!loading && !error && stats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No talent partners match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}